import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UploadSimple, Spinner, CheckCircle, WarningCircle, Images } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function BulkPhotosDialog({ open, onOpenChange, onUploaded }) {
  const [files, setFiles] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setFiles([]); setResult(null); setBusy(false); if (fileRef.current) fileRef.current.value = ""; };
  const handleClose = (o) => { if (!o) reset(); onOpenChange(o); };

  const onPick = (e) => { setFiles(Array.from(e.target.files || [])); setResult(null); };

  const upload = async () => {
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/assets/bulk-photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.uploaded > 0) { toast.success(`Uploaded ${data.uploaded} photo${data.uploaded === 1 ? "" : "s"}`); onUploaded?.(); }
      else toast.error("No photos matched a Stock #");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-photos-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Upload Photos</DialogTitle>
          <DialogDescription>Name each file with the asset's Stock # (e.g. <span className="font-mono">260311.jpg</span>, <span className="font-mono">260311-2.jpg</span>). Photos auto-attach to the matching asset.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className="border-2 border-dashed border-zinc-300 rounded-lg p-6 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors"
            onClick={() => fileRef.current?.click()}
            data-testid="photos-dropzone"
          >
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid="photos-file-input" />
            <Images size={32} className="mx-auto text-emerald-500" weight="duotone" />
            <p className="text-sm font-medium mt-2">{files.length ? `${files.length} photo${files.length === 1 ? "" : "s"} selected` : "Click to choose photos"}</p>
            <p className="text-xs text-zinc-400 mt-1">JPG or PNG. Filename = Stock #.</p>
          </div>

          {result && (
            <div className="rounded-lg border border-zinc-200 p-3 space-y-2 text-sm" data-testid="photos-result">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="flex items-center gap-1.5 font-semibold text-emerald-700"><CheckCircle size={18} weight="fill" /> {result.uploaded} uploaded</span>
                {result.unmatched.length > 0 && <span className="flex items-center gap-1.5 text-amber-600"><WarningCircle size={18} weight="fill" /> {result.unmatched.length} unmatched</span>}
              </div>
              {result.unmatched.length > 0 && (
                <div className="max-h-28 overflow-y-auto text-xs space-y-1 border-t border-zinc-100 pt-2 text-amber-600">
                  <p className="text-zinc-500">No asset with a matching Stock # for:</p>
                  {result.unmatched.map((f, i) => <div key={i} className="font-mono">{f}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} className="border-zinc-300">Close</Button>
          <Button onClick={upload} disabled={busy || !files.length} className="gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="confirm-photos-upload">
            {busy ? <Spinner size={16} className="animate-spin" /> : <UploadSimple size={16} weight="bold" />} Upload {files.length || ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
